import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { projectsApi } from "@/api/projects";
import { plansApi } from "@/api/plans";
import { usageApi } from "@/api/usage";

export const Route = createFileRoute("/app/projects/$pid/")({
  component: ProjectOverview,
});

// Server enforces this too; FE checks just to hide / disable the
// button outside the window. Keep in sync with dashboard-api
// /projects/:pid/renew RENEWAL_WINDOW_DAYS.
const RENEWAL_WINDOW_DAYS = 7;

function ProjectOverview() {
  const { pid } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: project } = useQuery({ queryKey: ["project", pid], queryFn: () => projectsApi.get(pid) });
  const { data: planRow } = useQuery({ queryKey: ["project-plan", pid], queryFn: () => plansApi.forProject(pid) });
  const { data: cu } = useQuery({ queryKey: ["usage-current-cycle", pid], queryFn: () => usageApi.currentCycle(pid) });

  const callerRole = project?.role;
  const canRenew = callerRole === "owner" || callerRole === "billing";
  const paidUntil = planRow?.paid_until ? new Date(planRow.paid_until) : null;
  const windowOpensAt = paidUntil
    ? new Date(paidUntil.getTime() - RENEWAL_WINDOW_DAYS * 24 * 60 * 60 * 1000)
    : null;
  const now = new Date();
  const inWindow = !!paidUntil && !!windowOpensAt && now >= windowOpensAt && now < paidUntil;
  const lapsed = !!paidUntil && now >= paidUntil;
  // Renewal is allowed in the last 7d of the cycle AND after it has
  // lapsed (project is 402'd until they pay).
  const renewable = inWindow || lapsed;

  // A renewal the user already kicked off. 'confirmed' = paid + queued
  // (rolls paid_until forward at cycle end — nothing left to do).
  // 'pending' = started but not yet paid (send them back to finish).
  const queued = planRow?.queued_renewal ?? null;
  const queuedConfirmed = queued?.status === "confirmed" ? queued : null;
  const queuedPending = queued?.status === "pending" ? queued : null;
  const renewalExtendsTo = queued ? new Date(queued.extends_to) : null;
  // A pending payment for a different plan than the project currently holds
  // is a plan change, not a same-plan renewal.
  const isPlanChange =
    !!queuedPending?.plan_id && queuedPending.plan_id !== planRow?.plan?.id;

  const activePromo = planRow?.active_promo ?? null;

  const [renewMsg, setRenewMsg] = useState<string | null>(null);
  const [renewErr, setRenewErr] = useState<string | null>(null);
  const [renewPromo, setRenewPromo] = useState("");
  const renew = useMutation({
    mutationFn: (promoCode?: string) => plansApi.renew(pid, promoCode),
    onSuccess: (r) => {
      setRenewErr(null);
      qc.invalidateQueries({ queryKey: ["project", pid] });
      qc.invalidateQueries({ queryKey: ["project-plan", pid] });
      // A comp renewal applies inline — no deposit address to collect.
      if (r.is_comp) {
        setRenewMsg("Renewal applied — your cycle has been extended at no charge.");
        return;
      }
      // Hand off to the payment page — the user needs a deposit address.
      void navigate({
        to: "/app/projects/$pid/pay/$paymentId",
        params: { pid, paymentId: r.payment_id },
      });
    },
    onError: (e: Error) => {
      setRenewMsg(null);
      setRenewErr(e.message);
    },
  });

  const cancelPayment = useMutation({
    mutationFn: (paymentId: string) => plansApi.cancelPayment(pid, paymentId),
    onSuccess: () => {
      setRenewErr(null);
      setRenewMsg("Cancelled. You can start a different change now.");
      qc.invalidateQueries({ queryKey: ["project-plan", pid] });
      qc.invalidateQueries({ queryKey: ["payments", pid] });
    },
    onError: (e: Error) => setRenewErr(e.message),
  });

  return (
    <div className="space-y-6">
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <Stat label="Plan" value={planRow?.plan?.display_name ?? "—"} />
        <Stat
          label="Paid until"
          value={
            paidUntil
              ? queuedConfirmed && renewalExtendsTo
                ? `${paidUntil.toLocaleDateString()} → ${renewalExtendsTo.toLocaleDateString()}`
                : paidUntil.toLocaleDateString()
              : "—"
          }
        />
        <Stat label="Status" value={project?.suspended ? "suspended" : "active"} />
      </div>

      {activePromo && (
        <div className="card flex flex-wrap items-center gap-2 border-l-4 border-l-green-500 text-sm">
          <span className="rounded-full bg-green-500/10 px-2 py-0.5 font-medium text-green-700">
            {activePromo.code}
          </span>
          <span className="text-ink/65">
            {activePromo.kind === "percent"
              ? `${activePromo.value}% off`
              : `$${activePromo.value} off`}{" "}
            {activePromo.scope === "lifetime" ? "every renewal" : "(one-time)"} — applied automatically.
          </span>
        </div>
      )}

      {cu && <CreditsBar used={cu.used} limit={cu.limit} resetsAt={cu.resets_at} cycleDays={cu.cycle_days} />}

      {canRenew && paidUntil && (
        queuedConfirmed && renewalExtendsTo ? (
          // Paid + queued: tell the user it's handled, no button.
          <div className="card flex flex-wrap items-center gap-4 border-l-4 border-l-green-500">
            <div className="text-sm text-ink/75">
              <b>Renewal paid.</b> Your plan extends to <b>{renewalExtendsTo.toLocaleDateString()}</b>{" "}
              automatically when the current cycle ends <b>{paidUntil.toLocaleDateString()}</b>. Nothing else to do.
            </div>
          </div>
        ) : queuedPending ? (
          // Started but unpaid: route straight to the pending payment's
          // deposit page. (Don't call renew — this payment may be a plan
          // change, and renew is window-gated and would reject.)
          <div className="card flex flex-wrap items-center gap-4 border-l-4 border-l-amber-500">
            <div className="text-sm text-ink/75">
              {isPlanChange ? (
                <>Plan change to <b>{queuedPending.plan_id}</b> started — <b>payment pending</b>. Finish paying to switch.</>
              ) : (
                <>Renewal started — <b>payment pending</b>. Finish paying to lock in your next cycle.</>
              )}
            </div>
            <div className="ml-auto flex gap-2">
              <button
                onClick={() => cancelPayment.mutate(queuedPending.payment_id)}
                disabled={cancelPayment.isPending}
                className="btn-secondary disabled:cursor-not-allowed disabled:opacity-50"
              >
                {cancelPayment.isPending ? "Cancelling…" : "Cancel"}
              </button>
              <button
                onClick={() =>
                  navigate({
                    to: "/app/projects/$pid/pay/$paymentId",
                    params: { pid, paymentId: queuedPending.payment_id },
                  })
                }
                className="btn-primary"
              >
                Complete payment
              </button>
            </div>
          </div>
        ) : (
          <div className="card flex flex-wrap items-center gap-4">
            <div className="text-sm text-ink/65">
              {lapsed ? (
                <>Cycle ended <b>{paidUntil.toLocaleDateString()}</b>. Renew to resume API access — a new cycle starts when payment is confirmed.</>
              ) : inWindow ? (
                <>Cycle ends <b>{paidUntil.toLocaleDateString()}</b>. Renew now to extend.</>
              ) : (
                <>Renewal opens {windowOpensAt!.toLocaleDateString()} ({RENEWAL_WINDOW_DAYS} days before cycle end).</>
              )}
            </div>
            {renewable && !activePromo && (
              <input
                className="input w-40"
                placeholder="Promo code"
                value={renewPromo}
                onChange={(e) => setRenewPromo(e.target.value)}
              />
            )}
            <button
              onClick={() => renew.mutate(renewPromo.trim() || undefined)}
              disabled={!renewable || renew.isPending}
              className="btn-primary ml-auto disabled:cursor-not-allowed disabled:opacity-50"
              title={
                renewable
                  ? "Renew this plan for another cycle"
                  : "Available in the last 7 days of your cycle"
              }
            >
              {renew.isPending ? "Requesting…" : "Renew"}
            </button>
          </div>
        )
      )}
      {renewMsg && <div className="card border-l-4 border-l-green-500 text-sm text-ink/75">{renewMsg}</div>}
      {renewErr && <div className="card border-l-4 border-l-red-500 text-sm text-red-700">{renewErr}</div>}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="card">
      <div className="text-xs uppercase tracking-wide text-ink/55">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function CreditsBar({
  used, limit, resetsAt, cycleDays,
}: { used: number; limit: number; resetsAt: string; cycleDays: number }) {
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  // Bar color tracks how close the project is to its cycle cap.
  // Matches the same thresholds backend-api uses for soft warnings.
  const tone =
    pct >= 100 ? "bg-red-500"
    : pct >= 80  ? "bg-amber-500"
    :              "bg-peach-500";
  return (
    <div className="card">
      <div className="flex items-baseline justify-between">
        <div className="text-xs uppercase tracking-wide text-ink/55">
          Credits this cycle ({cycleDays}d)
        </div>
        <div className="text-sm text-ink/55">
          resets {new Date(resetsAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
        </div>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="text-2xl font-semibold">{used.toLocaleString()}</div>
        <div className="text-sm text-ink/55">/ {limit.toLocaleString()} CU</div>
      </div>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-ink/4">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
